/**
 * PRD.307 — client WhatsApp QR asset registry.
 *
 * A client publishes its Meta-generated QR by dropping the PNG at:
 *
 *   apps/web-admin/src/clients/<org>/whatsapp-qr.png
 *
 * No code change is needed to add one. `import.meta.glob` collects whatever
 * exists at build time and Vite fingerprints each file as a normal bundled
 * asset, so the original PNG is served untouched.
 *
 * Why a glob rather than the static imports used by `LoginLeft.tsx`: one source
 * tree builds every client, so a static `import ... from './<org>/whatsapp-qr.png'`
 * would fail the build for every client that has not supplied one. The QR is
 * optional by design (FR3), and a missing file must be a hidden section, not a
 * broken build. Absent files are simply absent keys here.
 */

import { pickWhatsappQr, type WhatsappQrAssetMap } from '../pages/appointments/whatsapp-qr';

/** Raw glob result: './<org>/whatsapp-qr.png' → bundled URL. */
const modules = import.meta.glob('./*/whatsapp-qr.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/** Keyed by client id (the directory name), which matches VITE_CLIENT_ID. */
export const WHATSAPP_QR_ASSETS: WhatsappQrAssetMap = Object.fromEntries(
  Object.entries(modules).map(([path, url]) => [path.split('/')[1] ?? '', url]),
);

/**
 * The current client's QR, or null when this deployment has none.
 * `clientId` is passed in (not read from env here) so callers stay testable.
 */
export function resolveWhatsappQrUrl(clientId: string | undefined | null): string | null {
  return pickWhatsappQr(WHATSAPP_QR_ASSETS, clientId);
}
